<template>
	<v-menu v-model="menu" :close-on-content-click="false" offset-y left open-on-hover open-delay="100" close-delay="400">
		<template #activator="{ on, attrs }">
			<v-btn depressed icon rounded v-bind="attrs" v-on="on" @click="goToNotify">
				<v-badge :content="count" :value="count > 0" color="red" overlap>
					<v-icon dark> mdi-bell </v-icon>
				</v-badge>
			</v-btn>
		</template>

		<v-card width="400" class="notifications-menu">
			<div class="d-flex align-center px-4 py-3">
				<span class="subtitle-1 font-weight-bold">Notifiche</span>
				<v-chip v-if="count > 0" x-small color="primary" class="ml-2">{{ count }} {{ count === 1 ? 'nuova' : 'nuove' }}</v-chip>
				<v-spacer />
				<v-btn v-if="count > 0" icon small :loading="markingAll" title="Segna tutte come lette" @click="markAllRead">
					<v-icon small>mdi-check-all</v-icon>
				</v-btn>
			</div>
			<v-divider />

			<div v-if="count === 0" class="text-center px-6 py-8">
				<v-icon size="44" color="grey lighten-1">mdi-bell-off-outline</v-icon>
				<div class="subtitle-2 mt-3">Nessuna nuova notifica</div>
				<div class="caption grey--text mt-1">Qui compariranno i prossimi avvisi di marketplace, export e importazioni.</div>
			</div>

			<v-list v-else class="py-0 notifications-menu__list">
				<template v-for="(preview, index) of previews">
					<v-list-item :key="preview._id || index" class="notifications-menu__item align-start py-2">
						<v-list-item-avatar size="32" :color="typeMeta(preview.type).color" class="mt-1 mr-3">
							<v-icon small dark>{{ typeMeta(preview.type).icon }}</v-icon>
						</v-list-item-avatar>
						<v-list-item-content class="py-0">
							<div class="d-flex align-center">
								<span class="body-2 font-weight-bold text-truncate">{{ preview.provider }}</span>
								<v-spacer />
								<span class="caption grey--text ml-2 text-no-wrap">{{ formatDate(preview.created_at) }}</span>
							</div>
							<div class="body-2 notifications-menu__text" v-html="safeHtml(preview.title)" />
							<div class="notifications-menu__actions d-flex align-center mt-1">
								<!-- link a una pagina del BO (es. richieste premi): si apre qui, non come download -->
								<v-btn v-if="internalPath(preview.url)" x-small text color="primary" class="px-1" @click="openInternal(preview)">
									<v-icon left x-small>mdi-arrow-right</v-icon>
									Apri
								</v-btn>
								<v-btn v-else-if="safeUrl(preview.url)" :href="safeUrl(preview.url)" target="_blank" rel="noopener noreferrer" x-small text color="primary" class="px-1">
									<v-icon left x-small>mdi-download</v-icon>
									Scarica
								</v-btn>
								<v-spacer />
								<v-btn x-small text class="px-1" title="Segna come letta" @click="disablePreview(preview)">
									<v-icon left x-small>mdi-eye</v-icon>
									Letta
								</v-btn>
							</div>
						</v-list-item-content>
					</v-list-item>
					<v-divider v-if="index < previews.length - 1" :key="`divider-${preview._id || index}`" />
				</template>
			</v-list>

			<v-divider />
			<v-btn block text tile color="primary" class="py-5" @click="goToNotify">
				{{ $vuetify.lang.t('$vuetify.front.notifiche.all') }}
				<v-icon right small>mdi-arrow-right</v-icon>
			</v-btn>
		</v-card>
	</v-menu>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from 'vuex';
import { safeHtml, safeUrl, internalPath } from '@/utils/safeHtml';

const TYPE_META = {
	success: { icon: 'mdi-check', color: 'success' },
	error: { icon: 'mdi-close', color: 'error' },
	warning: { icon: 'mdi-exclamation', color: 'warning' },
	info: { icon: 'mdi-information-variant', color: 'primary' },
};

export default {
	name: 'NotificationsMenu',
	data() {
		return {
			menu: false,
			markingAll: false,
		};
	},
	mounted() {
		this.fetchNotifies();
	},
	computed: {
		...mapGetters({
			count: 'notify/getCount',
			previews: 'notify/getPreviews',
		}),
	},
	methods: {
		...mapMutations({
			setPreload: 'settings/setPreload',
		}),
		...mapActions({
			fetchNotifies: 'notify/asyncPreviewData',
			disableNotify: 'notify/disableItem',
		}),
		safeHtml,
		safeUrl,
		internalPath,
		// apre la pagina del BO e segna la notifica come letta
		async openInternal(item) {
			this.menu = false;
			const path = internalPath(item.url);
			if (path && this.$route.fullPath !== path) {
				this.$router.push(path);
			}
			await this.disableNotify(item);
			await this.fetchNotifies();
		},
		typeMeta(type) {
			return TYPE_META[type] || TYPE_META.info;
		},
		formatDate(date) {
			const day = this.$moment(date);
			if (day.isSame(this.$moment(), 'day')) {
				return `oggi ${day.format('HH:mm')}`;
			}
			if (day.isSame(this.$moment().subtract(1, 'day'), 'day')) {
				return `ieri ${day.format('HH:mm')}`;
			}
			return day.format('DD/MM HH:mm');
		},
		async disablePreview(item) {
			this.setPreload(true);
			await this.disableNotify(item);
			await this.fetchNotifies();
			this.setPreload(false);
		},
		// il backend aggiorna una notifica alla volta: si segnano lette quelle mostrate nel menu
		async markAllRead() {
			this.markingAll = true;
			await Promise.all(this.previews.slice().map(item => this.disableNotify(item)));
			await this.fetchNotifies();
			this.markingAll = false;
		},
		goToNotify() {
			this.menu = false;
			if (this.$route.path !== '/notify') {
				this.$router.push('/notify');
			}
		},
	},
};
</script>

<style scoped>
.notifications-menu__list {
	max-height: 400px;
	overflow-y: auto;
}
.notifications-menu__text {
	display: -webkit-box;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
	overflow: hidden;
	white-space: pre-wrap;
}
/* azioni visibili al passaggio del mouse; sempre visibili sui dispositivi touch */
@media (hover: hover) {
	.notifications-menu__actions {
		opacity: 0;
		transition: opacity 0.15s;
	}
	.notifications-menu__item:hover .notifications-menu__actions {
		opacity: 1;
	}
}
</style>
